import { useNavigate, useParams } from 'react-router-dom'
import { ComponentWithPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import DoubleTable from '../../../../../../../../baseComponents/DoubleTable'
import { useAddDoubleTableGreenRowRender } from '../../../../../../../../baseComponents/DoubleTable/functions'
import TitleAndWhiteBoxLayout from '../../../../../../../../baseComponents/Layout/TitleAndWhiteBoxLayout'
import { TwinTableLink } from '../../../../../../../../baseComponents/TwinTable'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import locations from '../../../../../../../../utils/globals/locations'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { SwitchButton, TabItems } from '../../../../../../../../baseComponents/Button/SwitchButton'
import { SetStateAction, useCallback, useState } from 'react'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { MultiSellInvoiceInversedKeys, MultiSellInvoiceTypeComponentTy } from './types'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import SellsPending from './SellsPending'

interface GenerateMultiSellInvoicesProps extends ComponentWithPermissions { }

const GenerateMultiSellInvoices: React.FC<GenerateMultiSellInvoicesProps> = ({ userPermissions }) => {
    const { CustomerId } = useParams()
    const { typeSell, handleTypeSell } = useGenerateMultiSellInvoicesLogic()

    return (<DoubleTable userPermissions={userPermissions} FirstChild={CustomersInner} SecondChild={SellsPending} displaySecond={CustomerId !== 'main'} extraData={{ typeSell, setTypeSell: handleTypeSell }} />)
}

const useGenerateMultiSellInvoicesLogic = () => {
    const [typeSell, setTypeSell] = useState<MultiSellInvoiceInversedKeys>(0)
    const navigate = useNavigate()
    const handleTypeSell = useCallback((value: MultiSellInvoiceInversedKeys ) => {
        setTypeSell(value)
        navigate(locations.sellInvoiceMulti +'/main')
    }, [setTypeSell, navigate])

    return { typeSell, handleTypeSell }
}

interface CustomersInnerProps {
    extraData: TwinDictionary
}
const CustomersInner: React.FC<CustomersInnerProps> = ({ extraData }) => {
    const { t } = useTwinTranslation()
    const dictCustomerSellTy: MultiSellInvoiceTypeComponentTy = {
        0: {
            TableComponent: TableCustomersSellOrders,
        },
        1: {
            TableComponent: TableCustomersSellDeliveryNotes,
        },
    }
    const TableComponent = dictCustomerSellTy[extraData?.typeSell as MultiSellInvoiceInversedKeys ]?.TableComponent 
    return (
        <TitleAndWhiteBoxLayout title={t('generateMultiInvoice', 'Generar factura múltiple')} RightHeader={<RightHeaderCustomerSellOrder typeSell={extraData?.typeSell as MultiSellInvoiceInversedKeys} setTypeSell={extraData?.setTypeSell}/>}>
            <TableComponent />
        </TitleAndWhiteBoxLayout>
    )
}




interface RightHeaderCustomerSellOrderProps  {
    typeSell: MultiSellInvoiceInversedKeys
    setTypeSell: React.Dispatch<SetStateAction<MultiSellInvoiceInversedKeys>>
 }

const RightHeaderCustomerSellOrder: React.FC<RightHeaderCustomerSellOrderProps> = ({typeSell, setTypeSell}) => {
    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'sellOrders'}>Pedidos</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'deliveryNotes'}>Albaranes</TwinTrans>
        },
    ]
    return (
        <SwitchButton className='mr-auto' onClick={(val) => setTypeSell(val as any)} active={typeSell} items={items} />
    )
}

interface TableCustomersSellOrdersProps { }

const TableCustomersSellOrders: React.FC<TableCustomersSellOrdersProps> = () => {
    const { t } = useTwinTranslation()
    const { CustomerId } = useParams()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(CustomerId || '')
    const columns: ColumnTableSchema<CustomerModelType> = [
        {
            id: 'code',
            dataKey: 'code',
            label: t('code', 'Código'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerCode', 'Código de cliente') }
            },
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
        },
        {
            id: 'fiscal_name',
            dataKey: 'fiscal_name',
            label: t('fiscalName', 'Nombre Fiscal'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('fiscalName', 'Nombre Fiscal') }
            }
        },
        {
            id: 'surname',
            dataKey: 'surname',
            label: t('surname', 'Apellidos'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('surname', 'Apellidos') }
            },
        },
    ]
    return (
        <TwinTableLink name='generateMultiSellInvoicesFromOrders' createRowDataLink={[locations.sellInvoiceMulti, ['$CustomerId']]} columns={columns} getDataFrom='/api/app/order/sell/sellOrder/getAllCustomersWithPendentSellOrders' extraWhereParams={{}} rowRenderer={rowRenderer} hideLoadFiltersButton={true} defaultOrder={{ defaultOrderField: 'CustomerId' }} />
    )
}


interface TableCustomersSellDeliveryNotesProps { }

const TableCustomersSellDeliveryNotes: React.FC<TableCustomersSellDeliveryNotesProps> = () => {
    const { t } = useTwinTranslation()
    const { CustomerId } = useParams()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(CustomerId || '')
    const columns: ColumnTableSchema<CustomerModelType> = [
        {
            id: 'code',
            dataKey: 'code',
            label: t('code', 'Código'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerCode', 'Código de cliente') }
            },
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
        },
        {
            id: 'fiscal_name',
            dataKey: 'fiscal_name',
            label: t('fiscalName', 'Nombre Fiscal'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('fiscalName', 'Nombre Fiscal') }
            }
        },
        {
            id: 'surname',
            dataKey: 'surname',
            label: t('surname', 'Apellidos'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('surname', 'Apellidos') }
            },
        },
    ]
    return (
        <TwinTableLink name='generateMultiSellInvoicesFromDeliveryNotes' createRowDataLink={[locations.sellInvoiceMulti, ['$CustomerId']]} columns={columns} getDataFrom='/api/app/order/sell/sellDeliveryNote/getAllCustomersWithPendentSellDeliveryNotes' extraWhereParams={{}} rowRenderer={rowRenderer} hideLoadFiltersButton={true} defaultOrder={{defaultOrderField: 'CustomerId'}} />
    )
}


export default GenerateMultiSellInvoices